import React, { useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface StockAdjustmentFormProps {
  variantId: number;
  companySchema: string;
  localId: number;
  currentStock: number;
  onClose: () => void;
  onSuccess: () => void;
}

export const StockAdjustmentForm: React.FC<StockAdjustmentFormProps> = ({
  variantId,
  companySchema,
  localId,
  currentStock,
  onClose,
  onSuccess
}) => {
  const [adjustmentType, setAdjustmentType] = useState<'increase' | 'decrease'>('increase');
  const [quantity, setQuantity] = useState('');
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quantity.trim() || !motivo.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const adjustmentQuantity = parseInt(quantity);
      const finalQuantity = adjustmentType === 'increase' 
        ? currentStock + adjustmentQuantity
        : currentStock - adjustmentQuantity;

      if (finalQuantity < 0) {
        throw new Error('El ajuste resultaría en un stock negativo');
      }

      // Create stock movement record
      const { error: movementError } = await supabase
        .schema(companySchema)
        .from('movimiento_de_stock')
        .insert({
          variante_id: variantId,
          cantidad: adjustmentType === 'increase' ? adjustmentQuantity : -adjustmentQuantity,
          motivo,
          local_id_origen: localId,
          local_id_destino: localId,
          tipo: 'ajuste',
          fecha: new Date().toISOString(),
          user_id: user.id
        });

      if (movementError) throw movementError;

      // Update stock
      const { error: stockError } = await supabase
        .schema(companySchema)
        .from('stock')
        .update({ cantidad_disponible: finalQuantity })
        .eq('variantes_id', variantId)
        .eq('local_id', localId);

      if (stockError) throw stockError;

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error adjusting stock:', err);
      setError(err instanceof Error ? err.message : 'Error al ajustar el stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <h3 className="text-lg font-medium text-gray-900 mb-6">
        Ajuste de stock
      </h3>
      
      <p className="text-gray-600 mb-6">
        Realiza una corrección en el stock. Utiliza esta función en caso de haber un error en la carga de datos, o en caso de que haya stock que se haya dañado o perdido
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Stock actual
          </label>
          <input
            type="text"
            value={currentStock}
            disabled
            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tipo de ajuste
          </label>
          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={() => setAdjustmentType('increase')}
              className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                adjustmentType === 'increase'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              Incrementar
            </button>
            <button
              type="button"
              onClick={() => setAdjustmentType('decrease')}
              className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                adjustmentType === 'decrease'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              Decrementar
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cantidad a incrementar/decrementar
          </label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Motivo del ajuste
          </label>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Explica el motivo del ajuste..."
            required
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !quantity || !motivo}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Ajustando...
              </>
            ) : (
              'Confirmar ajuste'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};